import React, { useState, useEffect, useRef } from 'react';
import { FaPhone, FaPhoneSlash, FaVolumeMute, FaUserPlus, FaTimes, FaBackspace, FaMicrophoneSlash, FaMicrophone, FaVideo, FaPause, FaPlay } from 'react-icons/fa';

const Dialpad = ({ phoneNumber, onClose, telnyxClient, currentCall, onEndCall, telnyxConnected, businessNumber = '+16478463028' }) => {
  const [currentNumber, setCurrentNumber] = useState('+1');
  const [callStatus, setCallStatus] = useState('idle'); // 'idle', 'dialing', 'incoming', 'calling', 'connected', 'held'
  const [incomingNumber, setIncomingNumber] = useState('');
  const [transferNumber, setTransferNumber] = useState('');
  const [callDuration, setCallDuration] = useState('00:00');
  const [ringingDuration, setRingingDuration] = useState('00:00'); // New: For ringing/connecting feedback
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isIncoming, setIsIncoming] = useState(false); // Track if call is inbound
  const [callerName, setCallerName] = useState('Unknown Caller'); // For display
  const [activityLog, setActivityLog] = useState([]);

  // Audio refs (kept for potential use, but not generating tones to match script)
  const audioContextRef = useRef(null);
  const callTimerRef = useRef(null);
  const ringingTimerRef = useRef(null); // New: For ringing timer
  const callStartTimeRef = useRef(null);
  const ringingStartTimeRef = useRef(null); // New: For ringing start time

  // Drag refs for call UI
  const callUiRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Logging function
  const log = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const entry = `[${timestamp}] ${message}`;
    console.log(`[Telnyx] ${message}`);
    setActivityLog(prev => [...prev, { entry, type }]);
  };

  const showError = (message, details = '') => {
    alert(`${message}${details ? '\n\n' + details : ''}`);
    log(`❌ ${message}`, 'error');
  };

  // Format duration helper
  const formatDuration = (startTime) => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const s = (elapsed % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Request microphone permission when connected
  useEffect(() => {
    if (telnyxConnected) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop());
          log('✅ Microphone permission granted', 'success');
        })
        .catch(e => {
          showError('Microphone Access Denied', 'Please allow microphone access in your browser settings.');
          log(`❌ Microphone permission denied: ${e.message}`, 'error');
        });
    }
  }, [telnyxConnected]);

  // Prefill with phone number if provided
  useEffect(() => {
    if (phoneNumber) {
      setCurrentNumber(phoneNumber);
      setIncomingNumber(phoneNumber);
      setCallerName(phoneNumber);
      if (callStatus === 'idle') {
        setCallStatus('dialing');
      }
    }
  }, [phoneNumber]);

  // Sync call status with Telnyx and set caller name (adapted from script's notification handler)
  useEffect(() => {
    if (currentCall) {
      const remoteNum = currentCall.options?.remoteCallerNumber || '';
      setIncomingNumber(remoteNum);
      setCallerName(remoteNum || 'Unknown Caller');

      const callState = currentCall.state;
      log("📡 Call State: " + callState);

      if (callState === 'trying') {
        setCallStatus('calling');
        if (!ringingStartTimeRef.current) {
          ringingStartTimeRef.current = Date.now();
          startRingingTimer();
        }
        log("⏳ Calling...");
      } else if (callState === 'ringing') {
        if (remoteNum) {
          setCallStatus('incoming');
          setIsIncoming(true); // Flag as inbound
          log("📞 INCOMING call from " + remoteNum);
        } else {
          log("📳 Outbound call ringing...");
          setCallStatus('calling');
          if (!ringingStartTimeRef.current) {
            ringingStartTimeRef.current = Date.now();
            startRingingTimer();
          }
        }
      } else if (callState === 'new') {
        log("📤 Outbound call initiated");
        setCallStatus('calling');
        ringingStartTimeRef.current = Date.now();
        startRingingTimer();
      } else if (callState === 'active') {
        if (callStatus !== 'connected' && callStatus !== 'held') {
          setCallStatus('connected');
          const remoteNumber = remoteNum || currentNumber || "Unknown";
          log("✅ Call ACTIVE - Showing call controls");
          // Stop ringing timer
          stopRingingTimer();
          ringingStartTimeRef.current = null;
          setRingingDuration('00:00');
          // Start call timer
          if (currentCall.remoteStream) {
            log('🔊 Remote audio stream attached successfully!', 'success');
            const remoteAudio = document.getElementById('remoteAudio');
            if (remoteAudio && !remoteAudio.srcObject) {
              remoteAudio.srcObject = currentCall.remoteStream;
              log('🔊 Manually set remoteAudio.srcObject', 'success');
            }
          } else {
            log('⚠️ No remote stream detected!', 'warning');
          }
          if (currentCall.localStream) {
            log('🎤 Local audio stream (microphone) attached!', 'success');
          }
          startCallTimer();
        }
      } else if (callState === 'held') {
        if (callStatus !== 'held') {
          setCallStatus('held');
          log('⏸️ Call held');
        }
      } else if (callState === 'hangup' || callState === 'destroy') {
        log("📴 Call ended");
        resetCallState();
      }
    } else {
      setCallStatus('idle');
    }
  }, [currentCall?.state]);

  // New: Start ringing timer
  const startRingingTimer = () => {
    stopRingingTimer();
    ringingTimerRef.current = setInterval(() => {
      if (ringingStartTimeRef.current) {
        setRingingDuration(formatDuration(ringingStartTimeRef.current));
      }
    }, 1000);
  };

  // New: Stop ringing timer
  const stopRingingTimer = () => {
    if (ringingTimerRef.current) clearInterval(ringingTimerRef.current);
    ringingTimerRef.current = null;
    setRingingDuration('00:00');
  };

  // Simulate call duration timer (for connected state)
  const startCallTimer = () => {
    stopCallTimer();
    callStartTimeRef.current = Date.now();
    callTimerRef.current = setInterval(() => {
      if (callStartTimeRef.current) {
        setCallDuration(formatDuration(callStartTimeRef.current));
      }
    }, 1000);
    log('⏱️ Call timer started', 'info');
  };

  const stopCallTimer = () => {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    callTimerRef.current = null;
    callStartTimeRef.current = null;
    setCallDuration('00:00');
  };

  // Reset call state (adapted from script)
  const resetCallState = () => {
    log("🔄 Resetting call state - Returning to idle screen");
    stopCallTimer();
    stopRingingTimer();
    setCallStatus('idle');
    setCurrentNumber('+1');
    setIncomingNumber('');
    setCallerName('Unknown Caller');
    setIsIncoming(false); // Reset flag
    setIsMuted(false);
    setIsOnHold(false);
    ringingStartTimeRef.current = null;
    onEndCall();
  };

  // Drag logic for call UI
  useEffect(() => {
    const handleMouseDown = (e) => {
      // Only enable drag when call is active (connected or held)
      if (callStatus !== 'connected' && callStatus !== 'held') return;
      setIsDragging(true);
      const rect = callUiRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault(); // Prevent text selection
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      if (callUiRef.current) {
        callUiRef.current.style.left = (e.clientX - dragOffset.x) + 'px';
        callUiRef.current.style.top = (e.clientY - dragOffset.y) + 'px';
        callUiRef.current.style.transform = 'none'; // Disable initial translate for drag
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const ui = callUiRef.current;
    if (ui) {
      ui.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      if (ui) ui.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, callStatus]);

  // Dial a number (adapted from addDigit)
  const dialNumber = (digit) => {
    if (callStatus === 'idle' || callStatus === 'dialing') {
      setCurrentNumber(prev => prev + digit);
      setCallStatus('dialing');
    }
  };

  const backspace = () => {
    setCurrentNumber(prev => prev.length > 2 ? prev.slice(0, -1) : '+1');
    if (currentNumber.length <= 2 && callStatus === 'dialing') {
      setCallStatus('idle');
    }
  };

// Update the handleNumberInputChange to handle backspace key (optional enhancement)
  const handleNumberInputChange = (e) => {
    const value = e.target.value.replace(/[^\d+*#]/g, '');
    setCurrentNumber(value);
    if (value && value.length > 1 && callStatus === 'idle') {
      setCallStatus('dialing');
    } else if (!value || value.length <= 1) {
      setCallStatus('idle');
    }
  };

  // Clear number (adapted from clearNumber)
  const clearNumber = () => {
    setCurrentNumber('+1');
    if (callStatus === 'dialing') {
      setCallStatus('idle');
    }
  };

  // Make call using Telnyx (outbound, adapted from makeCall)
  const makeCall = () => {
    let phoneNumber = currentNumber.trim();
    if (!phoneNumber || phoneNumber.length < 3) { 
      showError('Please enter a valid phone number'); 
      return; 
    }
    phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
    if (!phoneNumber.startsWith('+')) phoneNumber = '+' + phoneNumber;
    const e164Regex = /^\+[1-9]\d{9,14}$/;
    if (!e164Regex.test(phoneNumber)) { 
      showError('Invalid format! Use: +14375245844'); 
      return; 
    }
    if (!telnyxClient) { 
      showError('Not connected'); 
      return; 
    }
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    // Optimistically transition UI immediately
    setIsIncoming(false); // Flag as outbound
    setCallStatus('calling');
    log(`📞 Dialing ${phoneNumber}...`);
    // Ensure microphone permission (re-check async)
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        stream.getTracks().forEach(track => track.stop());
        log('✅ Microphone permission confirmed for outbound call', 'success');
        proceedWithCall(phoneNumber);
      })
      .catch(e => {
        // Revert UI on failure
        setCallStatus('dialing');
        showError('Microphone Access Required', 'Please allow microphone access to make calls.');
        log(`❌ Microphone permission denied for call: ${e.message}`, 'error');
      });
  };

  const proceedWithCall = (phoneNumber) => {
    try {
      const audioEl = document.getElementById('remoteAudio');
      if (!audioEl) {
        throw new Error('Remote audio element not found!');
      }
      log('✅ Remote audio element found', 'success');
      const callOptions = {
        destinationNumber: phoneNumber,
        callerNumber: businessNumber,
        remoteElement: 'remoteAudio'
      };
      const call = telnyxClient.newCall(callOptions);
      // Note: currentCall will be updated via parent notification handler
      // No setCallStatus here - already set optimistically
      log('✅ Call initiated with remote audio configured', 'success');
    } catch (error) {
      // Revert UI on failure
      setCallStatus('dialing');
      log(`❌ Call failed: ${error.message}`, 'error');
      showError('Call Error', error.message);
    }
  };

  // Answer incoming call (adapted from answer)
  const answerCall = () => {
    if (currentCall) {
      log("✅ Answering incoming call...");
      // Optimistically transition UI immediately
      setCallStatus('calling');
      ringingStartTimeRef.current = Date.now();
      startRingingTimer();
      // Ensure microphone permission (re-check async)
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          stream.getTracks().forEach(track => track.stop());
          log('✅ Microphone permission confirmed for inbound call', 'success');
          proceedWithAnswer();
        })
        .catch(e => {
          // Revert UI on failure
          setCallStatus('incoming');
          stopRingingTimer();
          ringingStartTimeRef.current = null;
          showError('Microphone Access Required', 'Please allow microphone access to answer calls.');
          log(`❌ Microphone permission denied for answer: ${e.message}`, 'error');
        });
    }
  };

  const proceedWithAnswer = () => {
    if (currentCall) {
      // CRITICAL: Set remote element before answering
      if (!currentCall.options || !currentCall.options.remoteElement) {
        currentCall.options = currentCall.options || {};
        currentCall.options.remoteElement = 'remoteAudio';
        log('🔊 Set call.options.remoteElement = remoteAudio', 'info');
      }
      const remoteAudio = document.getElementById('remoteAudio');
      if (remoteAudio && currentCall.remoteStream) {
        remoteAudio.srcObject = currentCall.remoteStream;
        log('🔊 Set remoteAudio.srcObject for incoming call', 'success');
      }
      try { 
        currentCall.answer(); 
        // No setCallStatus here - already set optimistically
        log("⏳ Waiting for call to become active...");
      } catch (e) { 
        // Revert UI on failure
        setCallStatus('incoming');
        stopRingingTimer();
        ringingStartTimeRef.current = null;
        log(`Answer error: ${e.message}`, 'error'); 
      }
    }
  };

  // Reject/End call (adapted from reject and hangup)
  const rejectCall = () => {
    if (currentCall) {
      log('❌ Rejecting incoming call');
      try { 
        currentCall.reject(); 
      } catch (e) { 
        log(`Reject error: ${e.message}`, 'error'); 
      }
    }
    resetCallState();
  };

  const endCall = () => {
    if (currentCall) {
      log("📴 Ending call...");
      try { 
        currentCall.hangup(); 
      } catch (e) { 
        log(`Hangup error: ${e.message}`, 'error'); 
      }
    }
    resetCallState();
  };

  // Transfer call (adapted from transfer)
  const transferCall = () => {
    if (currentCall) {
      log("🔄 Initiating transfer...");
      // TODO: Implement fetch to backend endpoint like in reference script
      // For now, just log (add agentName prop if needed for fetch)
      console.log(`Transferring call ${currentCall.id} to group`);
      // Optionally end call or handle response
    }
  };

  // Mute toggle (adapted from toggleMute)
  const toggleMute = () => {
    const newState = !isMuted;
    setIsMuted(newState);
    if (currentCall) {
      try {
        if (newState) {
          currentCall.muteAudio();
          log("🔇 Microphone muted");
        } else {
          currentCall.unmuteAudio();
          log("🔊 Microphone unmuted");
        }
      } catch (e) {
        log(`Mute error: ${e.message}`, 'warning');
      }
    }
  };

  // Hold toggle (adapted from toggleHold)
  const toggleHold = () => {
    const newState = !isOnHold;
    setIsOnHold(newState);
    if (currentCall) {
      try {
        if (newState) {
          currentCall.hold();
          log("⏸️ Call on hold");
        } else {
          currentCall.unhold();
          log("▶️ Call resumed");
        }
      } catch (e) {
        log(`Hold error: ${e.message}`, 'warning');
      }
    }
  };

  const stopDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  // Dialpad keys
  const keys = [
    { value: '1' },
    { value: '2' },
    { value: '3' },
    { value: '4' },
    { value: '5' },
    { value: '6' },
    { value: '7' },
    { value: '8' },
    { value: '9' },
    { value: '*' },
    { value: '0' },
    { value: '#' }
  ];

  const getDisplayNumber = () => {
    const displayNum = incomingNumber || currentNumber;
    return displayNum || '+1';
  };

  // Enable controls during calling, connected, held (except incoming)
  const isEnabled = callStatus !== 'incoming';

  // Get status text
  const getStatusText = () => {
    if (callStatus === 'incoming') return 'Incoming audio call';
    if (callStatus === 'calling') return `${isIncoming ? 'Connecting' : 'Ringing'}... ${ringingDuration}`;
    if (callStatus === 'held') return 'On Hold';
    return callDuration;
  };

  // Render circular call UI for incoming/calling/connected/held
  if (callStatus === 'incoming' || callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') {
    return (
      <>
        {/* Backdrop - Non-blocking overlay with subtle dimming */}
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.2)', 
            zIndex: 1001,
            pointerEvents: 'none'  // Allow clicks to pass through to underlying content
          }} 
        />
        {/* Circular Call UI - Draggable */}
        <div ref={callUiRef} style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          // transform: isDragging ? 'none' : 'translate(-50%, -50%)',
          // width: '300px',
          // height: '500px',
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', // Enhanced purple-blue gradient
          borderRadius: '20px',
          display: 'flex',
          // flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1002,
          color: 'white',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          padding: '10px',
          // boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          cursor: (callStatus === 'connected' || callStatus === 'held') ? 'move' : 'default',
          pointerEvents: 'auto'  // Ensure the UI captures events
        }}>
          {/* Top: Close button */}
          {/* <button onClick={endCall} onMouseDown={stopDrag} style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px', 
            background: 'none', 
            border: 'none', 
            color: 'white', 
            fontSize: '20px', 
            cursor: 'pointer' 
          }}>
            <FaTimes />
          </button> */}

          {/* Avatar */}
          <div >
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: '#ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '25px',
              color: '#999',
              border: '4px solid rgba(255,255,255,0.2)'
            }}>
              👤 {/* Placeholder avatar */}
            </div>
          </div>

          {/* Caller Name */}
          <div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', margin: '7px 4px' }}>
              {callerName}
            </div>

            {/* Status */}
            <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: isEnabled ? '7px 4px' : '7px 4px' }}>
              {getStatusText()}
            </div>
          </div>
          {/* Controls */}
          <div style={{ display: 'flex', gap: '7px', alignItems: 'center', margin:'0 7px'}}>
            {/* Mute and Hold for calling/connected/held */}
            {(callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') && (
              <>
                <button 
                  onClick={isEnabled ? toggleMute : undefined}
                  onMouseDown={stopDrag}
                  disabled={!isEnabled}
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isEnabled ? 'pointer' : 'not-allowed',
                    opacity: isEnabled ? 1 : 0.5
                  }}
                >
                  {isMuted ? <FaMicrophoneSlash size={20} /> : <FaMicrophone size={20} />}
                </button>
                <button 
                  onClick={isEnabled ? toggleHold : undefined}
                  onMouseDown={stopDrag}
                  disabled={!isEnabled}
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isEnabled ? 'pointer' : 'not-allowed',
                    opacity: isEnabled ? 1 : 0.5
                  }}
                >
                  {isOnHold ? <FaPlay size={20} /> : <FaPause size={20} />}
                </button>
              </>
            )}

            {/* Main Action Button */}
            <button
              onClick={callStatus === 'incoming' ? answerCall : endCall}
              onMouseDown={stopDrag}
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                border: 'none',
                background: callStatus === 'incoming' ? '#10b981' : '#ef4444', // Enhanced green/red
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}
            >
              {callStatus === 'incoming' ? <FaPhone /> : <FaPhoneSlash />}
            </button>

            {/* Decline for incoming */}
            {callStatus === 'incoming' && (
              <button onClick={rejectCall} onMouseDown={stopDrag} style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '24px'
              }}>
                <FaPhoneSlash />
              </button>
            )}
          </div>

          {/* Transfer Option for calling/connected/held */}
          {(callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') && (
            <button 
              onClick={isEnabled ? transferCall : undefined}
              onMouseDown={stopDrag}
              disabled={!isEnabled}
              style={{ 
                width: '100%', 
                padding: '6px', 
                borderRadius: '8px', 
                border: 'none', 
                background: isEnabled ? '#f59e0b' : 'rgba(245, 158, 11, 0.5)', 
                color: 'white', 
                fontSize: '10px', 
                cursor: isEnabled ? 'pointer' : 'not-allowed',
                opacity: isEnabled ? 1 : 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
            >
              <FaUserPlus size={14} />
              Transfer to Group
            </button>
          )}
        </div>
      </>
    );
  }

  // Default Dialpad UI for idle/dialing
  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          width: '420px',
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', // Enhanced blue gradient for vibrancy
          borderRadius: '24px',
          padding: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Header: Status Dot | Close */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 10px'
          }}>
            {/* Status Dot - Green if Telnyx connected */}
            <div style={{
              background: telnyxConnected ? '#10b981' : '#6b7280', // Enhanced green/gray
              color: 'white',
              borderRadius: '50%',
              width: '8px',
              height: '8px',
              display: 'block'
            }}></div>
            
            {/* Close Button */}
            <button onClick={onClose} style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: 'white'
            }}>
              <FaTimes />
            </button>
          </div>

          {/* Title */}
          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '18px' }}>Phone Number</h3>
          </div>

          {/* Number Input Display with Backspace */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <input
              type="text"
              value={currentNumber}
              onChange={handleNumberInputChange}
              placeholder="+1"
              style={{
                flex: 1,
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                boxSizing: 'border-box',
                fontSize: '24px',
                fontWeight: 'bold',
                textAlign: 'center',
                background: 'white',
                color: '#1f2937'
              }}
            />
            <button
              onClick={backspace}
              style={{
                marginLeft: '10px',
                width: '50px',
                height: '50px',
                borderRadius: '10px',
                border: '2px solid #e5e7eb',
                background: '#f9fafb',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                color: '#1f2937',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f9fafb';
                e.target.style.color = '#1f2937';
                e.target.style.borderColor = '#e5e7eb';
              }}
            >
              ⌫
            </button>
          </div>

          {/* Keypad Grid - Square keys, no ABC */}
          {(callStatus === 'idle' || callStatus === 'dialing') && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px',
              marginBottom: '20px',
              justifyItems: 'center'
            }}>
              {keys.map((key, index) => (
                <div key={index} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button
                    onClick={() => dialNumber(key.value)}
                    disabled={callStatus !== 'idle' && callStatus !== 'dialing'}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '12px', // Changed from '50%' to rounded square
                      border: '2px solid #e5e7eb',
                      background: '#f9fafb',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: callStatus !== 'idle' && callStatus !== 'dialing' ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (callStatus === 'idle' || callStatus === 'dialing') {
                        e.target.style.background = '#667eea';
                        e.target.style.color = 'white';
                        e.target.style.borderColor = '#667eea';
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#f9fafb';
                      e.target.style.color = '#1f2937';
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.transform = 'scale(1)';
                    }}
                  >
                    {key.value}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Call Button - Full width, no Clear */}
          {(callStatus === 'idle' || callStatus === 'dialing') && (
            <button
              onClick={makeCall}
              disabled={currentNumber.length <= 2 || !telnyxConnected}
              style={{
                width: '100%',
                padding: '15px',
                borderRadius: '12px',
                border: 'none',
                background: (currentNumber.length > 2 && telnyxConnected) ? '#10b981' : '#e5e7eb',
                color: 'white',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: (currentNumber.length > 2 && telnyxConnected) ? 'pointer' : 'not-allowed',
                opacity: (currentNumber.length > 2 && telnyxConnected) ? 1 : 0.5,
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => { 
                if (currentNumber.length > 2 && telnyxConnected) {
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; }}
            >
              📞 Call
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Dialpad;

// // changes after the statusthings

// import React, { useState, useEffect, useRef } from 'react';
// import { FaPhone, FaPhoneSlash, FaVolumeMute, FaUserPlus, FaTimes, FaBackspace, FaMicrophoneSlash, FaMicrophone, FaVideo, FaPause, FaPlay } from 'react-icons/fa';

// const Dialpad = ({ phoneNumber, onClose, telnyxClient, currentCall, onEndCall, telnyxConnected, businessNumber = '+16478463028', userStatus, onStatusUpdate }) => {
//   const [currentNumber, setCurrentNumber] = useState('+1');
//   const [callStatus, setCallStatus] = useState('idle'); // 'idle', 'dialing', 'incoming', 'calling', 'connected', 'held'
//   const [incomingNumber, setIncomingNumber] = useState('');
//   const [transferNumber, setTransferNumber] = useState('');
//   const [callDuration, setCallDuration] = useState('00:00');
//   const [ringingDuration, setRingingDuration] = useState('00:00'); // New: For ringing/connecting feedback
//   const [isMuted, setIsMuted] = useState(false);
//   const [isOnHold, setIsOnHold] = useState(false);
//   const [isIncoming, setIsIncoming] = useState(false); // Track if call is inbound
//   const [callerName, setCallerName] = useState('Unknown Caller'); // For display
//   const [activityLog, setActivityLog] = useState([]);
//   const [originalStatus, setOriginalStatus] = useState(null); // Track original user status before call

//   // Audio refs (kept for potential use, but not generating tones to match script)
//   const audioContextRef = useRef(null);
//   const callTimerRef = useRef(null);
//   const ringingTimerRef = useRef(null); // New: For ringing timer
//   const callStartTimeRef = useRef(null);
//   const ringingStartTimeRef = useRef(null); // New: For ringing start time

//   // Drag refs for call UI
//   const callUiRef = useRef(null);
//   const [isDragging, setIsDragging] = useState(false);
//   const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

//   // Logging function
//   const log = (message, type = 'info') => {
//     const timestamp = new Date().toLocaleTimeString();
//     const entry = `[${timestamp}] ${message}`;
//     console.log(`[Telnyx] ${message}`);
//     setActivityLog(prev => [...prev, { entry, type }]);
//   };

//   const showError = (message, details = '') => {
//     alert(`${message}${details ? '\n\n' + details : ''}`);
//     log(`❌ ${message}`, 'error');
//   };

//   // Format duration helper
//   const formatDuration = (startTime) => {
//     const elapsed = Math.floor((Date.now() - startTime) / 1000);
//     const m = Math.floor(elapsed / 60).toString().padStart(2, '0');
//     const s = (elapsed % 60).toString().padStart(2, '0');
//     return `${m}:${s}`;
//   };

//   // Request microphone permission when connected
//   useEffect(() => {
//     if (telnyxConnected) {
//       navigator.mediaDevices.getUserMedia({ audio: true })
//         .then(stream => {
//           stream.getTracks().forEach(track => track.stop());
//           log('✅ Microphone permission granted', 'success');
//         })
//         .catch(e => {
//           showError('Microphone Access Denied', 'Please allow microphone access in your browser settings.');
//           log(`❌ Microphone permission denied: ${e.message}`, 'error');
//         });
//     }
//   }, [telnyxConnected]);

//   // Prefill with phone number if provided
//   useEffect(() => {
//     if (phoneNumber) {
//       setCurrentNumber(phoneNumber);
//       setIncomingNumber(phoneNumber);
//       setCallerName(phoneNumber);
//       if (callStatus === 'idle') {
//         setCallStatus('dialing');
//       }
//     }
//   }, [phoneNumber]);

//   // Sync call status with Telnyx and set caller name (adapted from script's notification handler)
//   useEffect(() => {
//     if (currentCall) {
//       const remoteNum = currentCall.options?.remoteCallerNumber || '';
//       setIncomingNumber(remoteNum);
//       setCallerName(remoteNum || 'Unknown Caller');

//       const callState = currentCall.state;
//       log("📡 Call State: " + callState);

//       if (callState === 'trying') {
//         setCallStatus('calling');
//         if (!ringingStartTimeRef.current) {
//           ringingStartTimeRef.current = Date.now();
//           startRingingTimer();
//         }
//         // Outgoing call
//         if (originalStatus === null && userStatus && !isIncoming) {
//           setOriginalStatus(userStatus);
//           onStatusUpdate('Outgoing call');
//         }
//         log("⏳ Calling...");
//       } else if (callState === 'ringing') {
//         if (remoteNum) {
//           setCallStatus('incoming');
//           setIsIncoming(true); // Flag as inbound
//           // Incoming call
//           if (originalStatus === null && userStatus) {
//             setOriginalStatus(userStatus);
//             onStatusUpdate('Incoming call');
//           }
//           log("📞 INCOMING call from " + remoteNum);
//         } else {
//           log("📳 Outbound call ringing...");
//           setCallStatus('calling');
//           if (!ringingStartTimeRef.current) {
//             ringingStartTimeRef.current = Date.now();
//             startRingingTimer();
//           }
//           // Outgoing call
//           if (originalStatus === null && userStatus && !isIncoming) {
//             setOriginalStatus(userStatus);
//             onStatusUpdate('Outgoing call');
//           }
//         }
//       } else if (callState === 'new') {
//         log("📤 Outbound call initiated");
//         setCallStatus('calling');
//         ringingStartTimeRef.current = Date.now();
//         startRingingTimer();
//         // Outgoing call
//         if (originalStatus === null && userStatus && !isIncoming) {
//           setOriginalStatus(userStatus);
//           onStatusUpdate('Outgoing call');
//         }
//       } else if (callState === 'active') {
//         if (callStatus !== 'connected' && callStatus !== 'held') {
//           setCallStatus('connected');
//           const remoteNumber = remoteNum || currentNumber || "Unknown";
//           log("✅ Call ACTIVE - Showing call controls");
//           // Stop ringing timer
//           stopRingingTimer();
//           ringingStartTimeRef.current = null;
//           setRingingDuration('00:00');
//           // Start call timer
//           if (currentCall.remoteStream) {
//             log('🔊 Remote audio stream attached successfully!', 'success');
//             const remoteAudio = document.getElementById('remoteAudio');
//             if (remoteAudio && !remoteAudio.srcObject) {
//               remoteAudio.srcObject = currentCall.remoteStream;
//               log('🔊 Manually set remoteAudio.srcObject', 'success');
//             }
//           } else {
//             log('⚠️ No remote stream detected!', 'warning');
//           }
//           if (currentCall.localStream) {
//             log('🎤 Local audio stream (microphone) attached!', 'success');
//           }
//           startCallTimer();
//           // Update status to In call
//           if (originalStatus !== null) {
//             onStatusUpdate('In call');
//           }
//         }
//       } else if (callState === 'held') {
//         if (callStatus !== 'held') {
//           setCallStatus('held');
//           log('⏸️ Call held');
//           // Optionally update to 'On hold' if needed
//         }
//       } else if (callState === 'hangup' || callState === 'destroy') {
//         log("📴 Call ended");
//         resetCallState();
//       }
//     } else {
//       setCallStatus('idle');
//     }
//   }, [currentCall?.state]);

//   // New: Start ringing timer
//   const startRingingTimer = () => {
//     stopRingingTimer();
//     ringingTimerRef.current = setInterval(() => {
//       if (ringingStartTimeRef.current) {
//         setRingingDuration(formatDuration(ringingStartTimeRef.current));
//       }
//     }, 1000);
//   };

//   // New: Stop ringing timer
//   const stopRingingTimer = () => {
//     if (ringingTimerRef.current) clearInterval(ringingTimerRef.current);
//     ringingTimerRef.current = null;
//     setRingingDuration('00:00');
//   };

//   // Simulate call duration timer (for connected state)
//   const startCallTimer = () => {
//     stopCallTimer();
//     callStartTimeRef.current = Date.now();
//     callTimerRef.current = setInterval(() => {
//       if (callStartTimeRef.current) {
//         setCallDuration(formatDuration(callStartTimeRef.current));
//       }
//     }, 1000);
//     log('⏱️ Call timer started', 'info');
//   };

//   const stopCallTimer = () => {
//     if (callTimerRef.current) clearInterval(callTimerRef.current);
//     callTimerRef.current = null;
//     callStartTimeRef.current = null;
//     setCallDuration('00:00');
//   };

//   // Reset call state (adapted from script)
//   const resetCallState = () => {
//     log("🔄 Resetting call state - Returning to idle screen");
//     stopCallTimer();
//     stopRingingTimer();
//     // Restore original status if there was a call
//     if (originalStatus !== null) {
//       onStatusUpdate(originalStatus);
//       setOriginalStatus(null);
//     }
//     setCallStatus('idle');
//     setCurrentNumber('+1');
//     setIncomingNumber('');
//     setCallerName('Unknown Caller');
//     setIsIncoming(false); // Reset flag
//     setIsMuted(false);
//     setIsOnHold(false);
//     ringingStartTimeRef.current = null;
//     onEndCall();
//   };

//   // Drag logic for call UI
//   useEffect(() => {
//     const handleMouseDown = (e) => {
//       // Only enable drag when call is active (connected or held)
//       if (callStatus !== 'connected' && callStatus !== 'held') return;
//       setIsDragging(true);
//       const rect = callUiRef.current.getBoundingClientRect();
//       setDragOffset({
//         x: e.clientX - rect.left,
//         y: e.clientY - rect.top
//       });
//       e.preventDefault(); // Prevent text selection
//     };

//     const handleMouseMove = (e) => {
//       if (!isDragging) return;
//       if (callUiRef.current) {
//         callUiRef.current.style.left = (e.clientX - dragOffset.x) + 'px';
//         callUiRef.current.style.top = (e.clientY - dragOffset.y) + 'px';
//         callUiRef.current.style.transform = 'none'; // Disable initial translate for drag
//       }
//     };

//     const handleMouseUp = () => {
//       setIsDragging(false);
//     };

//     const ui = callUiRef.current;
//     if (ui) {
//       ui.addEventListener('mousedown', handleMouseDown);
//       document.addEventListener('mousemove', handleMouseMove);
//       document.addEventListener('mouseup', handleMouseUp);
//     }

//     return () => {
//       if (ui) ui.removeEventListener('mousedown', handleMouseDown);
//       document.removeEventListener('mousemove', handleMouseMove);
//       document.removeEventListener('mouseup', handleMouseUp);
//     };
//   }, [isDragging, dragOffset, callStatus]);

//   // Dial a number (adapted from addDigit)
//   const dialNumber = (digit) => {
//     if (callStatus === 'idle' || callStatus === 'dialing') {
//       setCurrentNumber(prev => prev + digit);
//       setCallStatus('dialing');
//     }
//   };

//   const backspace = () => {
//     setCurrentNumber(prev => prev.length > 2 ? prev.slice(0, -1) : '+1');
//     if (currentNumber.length <= 2 && callStatus === 'dialing') {
//       setCallStatus('idle');
//     }
//   };

// // Update the handleNumberInputChange to handle backspace key (optional enhancement)
//   const handleNumberInputChange = (e) => {
//     const value = e.target.value.replace(/[^\d+*#]/g, '');
//     setCurrentNumber(value);
//     if (value && value.length > 1 && callStatus === 'idle') {
//       setCallStatus('dialing');
//     } else if (!value || value.length <= 1) {
//       setCallStatus('idle');
//     }
//   };

//   // Clear number (adapted from clearNumber)
//   const clearNumber = () => {
//     setCurrentNumber('+1');
//     if (callStatus === 'dialing') {
//       setCallStatus('idle');
//     }
//   };

//   // Make call using Telnyx (outbound, adapted from makeCall)
//   const makeCall = () => {
//     let phoneNumber = currentNumber.trim();
//     if (!phoneNumber || phoneNumber.length < 3) { 
//       showError('Please enter a valid phone number'); 
//       return; 
//     }
//     phoneNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');
//     if (!phoneNumber.startsWith('+')) phoneNumber = '+' + phoneNumber;
//     const e164Regex = /^\+[1-9]\d{9,14}$/;
//     if (!e164Regex.test(phoneNumber)) { 
//       showError('Invalid format! Use: +14375245844'); 
//       return; 
//     }
//     if (!telnyxClient) { 
//       showError('Not connected'); 
//       return; 
//     }
//     if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
//       audioContextRef.current.resume();
//     }
//     // Optimistically transition UI immediately
//     setIsIncoming(false); // Flag as outbound
//     setCallStatus('calling');
//     // Set original status and update to Outgoing call
//     if (originalStatus === null && userStatus) {
//       setOriginalStatus(userStatus);
//       onStatusUpdate('Outgoing call');
//     }
//     log(`📞 Dialing ${phoneNumber}...`);
//     // Ensure microphone permission (re-check async)
//     navigator.mediaDevices.getUserMedia({ audio: true })
//       .then(stream => {
//         stream.getTracks().forEach(track => track.stop());
//         log('✅ Microphone permission confirmed for outbound call', 'success');
//         proceedWithCall(phoneNumber);
//       })
//       .catch(e => {
//         // Revert UI on failure
//         setCallStatus('dialing');
//         // Revert status on failure
//         if (originalStatus !== null) {
//           onStatusUpdate(originalStatus);
//           setOriginalStatus(null);
//         }
//         showError('Microphone Access Required', 'Please allow microphone access to make calls.');
//         log(`❌ Microphone permission denied for call: ${e.message}`, 'error');
//       });
//   };

//   const proceedWithCall = (phoneNumber) => {
//     try {
//       const audioEl = document.getElementById('remoteAudio');
//       if (!audioEl) {
//         throw new Error('Remote audio element not found!');
//       }
//       log('✅ Remote audio element found', 'success');
//       const callOptions = {
//         destinationNumber: phoneNumber,
//         callerNumber: businessNumber,
//         remoteElement: 'remoteAudio'
//       };
//       const call = telnyxClient.newCall(callOptions);
//       // Note: currentCall will be updated via parent notification handler
//       // No setCallStatus here - already set optimistically
//       log('✅ Call initiated with remote audio configured', 'success');
//     } catch (error) {
//       // Revert UI on failure
//       setCallStatus('dialing');
//       // Revert status on failure
//       if (originalStatus !== null) {
//         onStatusUpdate(originalStatus);
//         setOriginalStatus(null);
//       }
//       log(`❌ Call failed: ${error.message}`, 'error');
//       showError('Call Error', error.message);
//     }
//   };

//   // Answer incoming call (adapted from answer)
//   const answerCall = () => {
//     if (currentCall) {
//       log("✅ Answering incoming call...");
//       // Optimistically transition UI immediately
//       setCallStatus('calling');
//       ringingStartTimeRef.current = Date.now();
//       startRingingTimer();
//       // Ensure microphone permission (re-check async)
//       navigator.mediaDevices.getUserMedia({ audio: true })
//         .then(stream => {
//           stream.getTracks().forEach(track => track.stop());
//           log('✅ Microphone permission confirmed for inbound call', 'success');
//           proceedWithAnswer();
//         })
//         .catch(e => {
//           // Revert UI on failure
//           setCallStatus('incoming');
//           stopRingingTimer();
//           ringingStartTimeRef.current = null;
//           // Revert status on failure
//           if (originalStatus !== null) {
//             onStatusUpdate(originalStatus);
//             setOriginalStatus(null);
//           }
//           showError('Microphone Access Required', 'Please allow microphone access to answer calls.');
//           log(`❌ Microphone permission denied for answer: ${e.message}`, 'error');
//         });
//     }
//   };

//   const proceedWithAnswer = () => {
//     if (currentCall) {
//       // CRITICAL: Set remote element before answering
//       if (!currentCall.options || !currentCall.options.remoteElement) {
//         currentCall.options = currentCall.options || {};
//         currentCall.options.remoteElement = 'remoteAudio';
//         log('🔊 Set call.options.remoteElement = remoteAudio', 'info');
//       }
//       const remoteAudio = document.getElementById('remoteAudio');
//       if (remoteAudio && currentCall.remoteStream) {
//         remoteAudio.srcObject = currentCall.remoteStream;
//         log('🔊 Set remoteAudio.srcObject for incoming call', 'success');
//       }
//       try { 
//         currentCall.answer(); 
//         // No setCallStatus here - already set optimistically
//         log("⏳ Waiting for call to become active...");
//       } catch (e) { 
//         // Revert UI on failure
//         setCallStatus('incoming');
//         stopRingingTimer();
//         ringingStartTimeRef.current = null;
//         // Revert status on failure
//         if (originalStatus !== null) {
//           onStatusUpdate(originalStatus);
//           setOriginalStatus(null);
//         }
//         log(`Answer error: ${e.message}`, 'error'); 
//       }
//     }
//   };

//   // Reject/End call (adapted from reject and hangup)
//   const rejectCall = () => {
//     if (currentCall) {
//       log('❌ Rejecting incoming call');
//       try { 
//         currentCall.reject(); 
//       } catch (e) { 
//         log(`Reject error: ${e.message}`, 'error'); 
//       }
//     }
//     resetCallState();
//   };

//   const endCall = () => {
//     if (currentCall) {
//       log("📴 Ending call...");
//       try { 
//         currentCall.hangup(); 
//       } catch (e) { 
//         log(`Hangup error: ${e.message}`, 'error'); 
//       }
//     }
//     resetCallState();
//   };

//   // Transfer call (adapted from transfer)
//   const transferCall = () => {
//     if (currentCall) {
//       log("🔄 Initiating transfer...");
//       // TODO: Implement fetch to backend endpoint like in reference script
//       // For now, just log (add agentName prop if needed for fetch)
//       console.log(`Transferring call ${currentCall.id} to group`);
//       // Optionally end call or handle response
//     }
//   };

//   // Mute toggle (adapted from toggleMute)
//   const toggleMute = () => {
//     const newState = !isMuted;
//     setIsMuted(newState);
//     if (currentCall) {
//       try {
//         if (newState) {
//           currentCall.muteAudio();
//           log("🔇 Microphone muted");
//         } else {
//           currentCall.unmuteAudio();
//           log("🔊 Microphone unmuted");
//         }
//       } catch (e) {
//         log(`Mute error: ${e.message}`, 'warning');
//       }
//     }
//   };

//   // Hold toggle (adapted from toggleHold)
//   const toggleHold = () => {
//     const newState = !isOnHold;
//     setIsOnHold(newState);
//     if (currentCall) {
//       try {
//         if (newState) {
//           currentCall.hold();
//           log("⏸️ Call on hold");
//         } else {
//           currentCall.unhold();
//           log("▶️ Call resumed");
//         }
//       } catch (e) {
//         log(`Hold error: ${e.message}`, 'warning');
//       }
//     }
//   };

//   const stopDrag = (e) => {
//     e.stopPropagation();
//     e.preventDefault();
//   };

//   // Dialpad keys
//   const keys = [
//     { value: '1' },
//     { value: '2' },
//     { value: '3' },
//     { value: '4' },
//     { value: '5' },
//     { value: '6' },
//     { value: '7' },
//     { value: '8' },
//     { value: '9' },
//     { value: '*' },
//     { value: '0' },
//     { value: '#' }
//   ];

//   const getDisplayNumber = () => {
//     const displayNum = incomingNumber || currentNumber;
//     return displayNum || '+1';
//   };

//   // Enable controls during calling, connected, held (except incoming)
//   const isEnabled = callStatus !== 'incoming';

//   // Get status text
//   const getStatusText = () => {
//     if (callStatus === 'incoming') return 'Incoming audio call';
//     if (callStatus === 'calling') return `${isIncoming ? 'Connecting' : 'Ringing'}... ${ringingDuration}`;
//     if (callStatus === 'held') return 'On Hold';
//     return callDuration;
//   };

//   // Render circular call UI for incoming/calling/connected/held
//   if (callStatus === 'incoming' || callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') {
//     return (
//       <>
//         {/* Backdrop - Non-blocking overlay with subtle dimming */}
//         <div 
//           style={{ 
//             position: 'fixed', 
//             top: 0, 
//             left: 0, 
//             width: '100%', 
//             height: '100%',
//             backgroundColor: 'rgba(0,0,0,0.2)', 
//             zIndex: 1001,
//             pointerEvents: 'none'  // Allow clicks to pass through to underlying content
//           }} 
//         />
//         {/* Circular Call UI - Draggable */}
//         <div ref={callUiRef} style={{
//           position: 'fixed',
//           left: '50%',
//           top: '50%',
//           // transform: isDragging ? 'none' : 'translate(-50%, -50%)',
//           // width: '300px',
//           // height: '500px',
//           background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', // Enhanced purple-blue gradient
//           borderRadius: '20px',
//           display: 'flex',
//           // flexDirection: 'column',
//           justifyContent: 'space-between',
//           alignItems: 'center',
//           zIndex: 1002,
//           color: 'white',
//           fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
//           padding: '10px',
//           // boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
//           cursor: (callStatus === 'connected' || callStatus === 'held') ? 'move' : 'default',
//           pointerEvents: 'auto'  // Ensure the UI captures events
//         }}>
//           {/* Top: Close button */}
//           {/* <button onClick={endCall} onMouseDown={stopDrag} style={{ 
//             position: 'absolute', 
//             top: '10px', 
//             right: '10px', 
//             background: 'none', 
//             border: 'none', 
//             color: 'white', 
//             fontSize: '20px', 
//             cursor: 'pointer' 
//           }}>
//             <FaTimes />
//           </button> */}

//           {/* Avatar */}
//           <div >
//             <div style={{
//               width: '50px',
//               height: '50px',
//               borderRadius: '50%',
//               background: '#ddd',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               fontSize: '25px',
//               color: '#999',
//               border: '4px solid rgba(255,255,255,0.2)'
//             }}>
//               👤 {/* Placeholder avatar */}
//             </div>
//           </div>

//           {/* Caller Name */}
//           <div>
//             <div style={{ fontSize: '14px', fontWeight: 'bold', margin: '7px 4px' }}>
//               {callerName}
//             </div>

//             {/* Status */}
//             <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: isEnabled ? '7px 4px' : '7px 4px' }}>
//               {getStatusText()}
//             </div>
//           </div>
//           {/* Controls */}
//           <div style={{ display: 'flex', gap: '7px', alignItems: 'center', margin:'0 7px'}}>
//             {/* Mute and Hold for calling/connected/held */}
//             {(callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') && (
//               <>
//                 <button 
//                   onClick={isEnabled ? toggleMute : undefined}
//                   onMouseDown={stopDrag}
//                   disabled={!isEnabled}
//                   style={{
//                     width: '30px',
//                     height: '30px',
//                     borderRadius: '50%',
//                     background: 'rgba(255,255,255,0.2)',
//                     border: 'none',
//                     color: 'white',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     cursor: isEnabled ? 'pointer' : 'not-allowed',
//                     opacity: isEnabled ? 1 : 0.5
//                   }}
//                 >
//                   {isMuted ? <FaMicrophoneSlash size={20} /> : <FaMicrophone size={20} />}
//                 </button>
//                 <button 
//                   onClick={isEnabled ? toggleHold : undefined}
//                   onMouseDown={stopDrag}
//                   disabled={!isEnabled}
//                   style={{
//                     width: '30px',
//                     height: '30px',
//                     borderRadius: '50%',
//                     background: 'rgba(255,255,255,0.2)',
//                     border: 'none',
//                     color: 'white',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center',
//                     cursor: isEnabled ? 'pointer' : 'not-allowed',
//                     opacity: isEnabled ? 1 : 0.5
//                   }}
//                 >
//                   {isOnHold ? <FaPlay size={20} /> : <FaPause size={20} />}
//                 </button>
//               </>
//             )}

//             {/* Main Action Button */}
//             <button
//               onClick={callStatus === 'incoming' ? answerCall : endCall}
//               onMouseDown={stopDrag}
//               style={{
//                 width: '30px',
//                 height: '30px',
//                 borderRadius: '50%',
//                 border: 'none',
//                 background: callStatus === 'incoming' ? '#10b981' : '#ef4444', // Enhanced green/red
//                 color: 'white',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 cursor: 'pointer',
//                 fontSize: '24px',
//                 boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
//               }}
//             >
//               {callStatus === 'incoming' ? <FaPhone /> : <FaPhoneSlash />}
//             </button>

//             {/* Decline for incoming */}
//             {callStatus === 'incoming' && (
//               <button onClick={rejectCall} onMouseDown={stopDrag} style={{
//                 width: '30px',
//                 height: '30px',
//                 borderRadius: '50%',
//                 background: 'rgba(255,255,255,0.2)',
//                 border: 'none',
//                 color: 'white',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 cursor: 'pointer',
//                 fontSize: '24px'
//               }}>
//                 <FaPhoneSlash />
//               </button>
//             )}
//           </div>

//           {/* Transfer Option for calling/connected/held */}
//           {(callStatus === 'calling' || callStatus === 'connected' || callStatus === 'held') && (
//             <button 
//               onClick={isEnabled ? transferCall : undefined}
//               onMouseDown={stopDrag}
//               disabled={!isEnabled}
//               style={{ 
//                 width: '100%', 
//                 padding: '6px', 
//                 borderRadius: '8px', 
//                 border: 'none', 
//                 background: isEnabled ? '#f59e0b' : 'rgba(245, 158, 11, 0.5)', 
//                 color: 'white', 
//                 fontSize: '10px', 
//                 cursor: isEnabled ? 'pointer' : 'not-allowed',
//                 opacity: isEnabled ? 1 : 0.5,
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 gap: '4px'
//               }}
//             >
//               <FaUserPlus size={14} />
//               Transfer to Group
//             </button>
//           )}
//         </div>
//       </>
//     );
//   }

//   // Default Dialpad UI for idle/dialing
//   return (
//     <>
//       <div style={{
//         position: 'fixed',
//         top: 0,
//         left: 0,
//         width: '100%',
//         height: '100%',
//         backgroundColor: 'rgba(0,0,0,0.5)',
//         zIndex: 1000,
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center'
//       }}>
//         <div style={{
//           width: '420px',
//           background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)', // Enhanced blue gradient for vibrancy
//           borderRadius: '24px',
//           padding: '20px',
//           boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
//           fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
//           position: 'relative',
//           overflow: 'hidden'
//         }}>
//           {/* Header: Status Dot | Close */}
//           <div style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             padding: '0 10px'
//           }}>
//             {/* Status Dot - Green if Telnyx connected */}
//             <div style={{
//               background: telnyxConnected ? '#10b981' : '#6b7280', // Enhanced green/gray
//               color: 'white',
//               borderRadius: '50%',
//               width: '8px',
//               height: '8px',
//               display: 'block'
//             }}></div>
            
//             {/* Close Button */}
//             <button onClick={onClose} style={{
//               background: 'none',
//               border: 'none',
//               fontSize: '20px',
//               cursor: 'pointer',
//               color: 'white'
//             }}>
//               <FaTimes />
//             </button>
//           </div>

//           {/* Title */}
//           <div style={{ textAlign: 'center', marginBottom: '10px' }}>
//             <h3 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '18px' }}>Phone Number</h3>
//           </div>

//           {/* Number Input Display with Backspace */}
//           <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
//             <input
//               type="text"
//               value={currentNumber}
//               onChange={handleNumberInputChange}
//               placeholder="+1"
//               style={{
//                 flex: 1,
//                 padding: '12px',
//                 border: '2px solid #e5e7eb',
//                 borderRadius: '10px',
//                 boxSizing: 'border-box',
//                 fontSize: '24px',
//                 fontWeight: 'bold',
//                 textAlign: 'center',
//                 background: 'white',
//                 color: '#1f2937'
//               }}
//             />
//             <button
//               onClick={backspace}
//               style={{
//                 marginLeft: '10px',
//                 width: '50px',
//                 height: '50px',
//                 borderRadius: '10px',
//                 border: '2px solid #e5e7eb',
//                 background: '#f9fafb',
//                 cursor: 'pointer',
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'center',
//                 fontSize: '18px',
//                 color: '#1f2937',
//                 transition: 'all 0.2s'
//               }}
//               onMouseEnter={(e) => {
//                 e.target.style.background = '#667eea';
//                 e.target.style.color = 'white';
//                 e.target.style.borderColor = '#667eea';
//               }}
//               onMouseLeave={(e) => {
//                 e.target.style.background = '#f9fafb';
//                 e.target.style.color = '#1f2937';
//                 e.target.style.borderColor = '#e5e7eb';
//               }}
//             >
//               ⌫
//             </button>
//           </div>

//           {/* Keypad Grid - Square keys, no ABC */}
//           {(callStatus === 'idle' || callStatus === 'dialing') && (
//             <div style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(3, 1fr)',
//               gap: '12px',
//               marginBottom: '20px',
//               justifyItems: 'center'
//             }}>
//               {keys.map((key, index) => (
//                 <div key={index} style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                   <button
//                     onClick={() => dialNumber(key.value)}
//                     disabled={callStatus !== 'idle' && callStatus !== 'dialing'}
//                     style={{
//                       width: '70px',
//                       height: '70px',
//                       borderRadius: '12px', // Changed from '50%' to rounded square
//                       border: '2px solid #e5e7eb',
//                       background: '#f9fafb',
//                       fontSize: '24px',
//                       fontWeight: 'bold',
//                       cursor: 'pointer',
//                       color: '#1f2937',
//                       display: 'flex',
//                       alignItems: 'center',
//                       justifyContent: 'center',
//                       opacity: callStatus !== 'idle' && callStatus !== 'dialing' ? 0.5 : 1,
//                       transition: 'all 0.2s'
//                     }}
//                     onMouseEnter={(e) => {
//                       if (callStatus === 'idle' || callStatus === 'dialing') {
//                         e.target.style.background = '#667eea';
//                         e.target.style.color = 'white';
//                         e.target.style.borderColor = '#667eea';
//                         e.target.style.transform = 'scale(1.05)';
//                       }
//                     }}
//                     onMouseLeave={(e) => {
//                       e.target.style.background = '#f9fafb';
//                       e.target.style.color = '#1f2937';
//                       e.target.style.borderColor = '#e5e7eb';
//                       e.target.style.transform = 'scale(1)';
//                     }}
//                   >
//                     {key.value}
//                   </button>
//                 </div>
//               ))}
//             </div>
//           )}

//           {/* Call Button - Full width, no Clear */}
//           {(callStatus === 'idle' || callStatus === 'dialing') && (
//             <button
//               onClick={makeCall}
//               disabled={currentNumber.length <= 2 || !telnyxConnected}
//               style={{
//                 width: '100%',
//                 padding: '15px',
//                 borderRadius: '12px',
//                 border: 'none',
//                 background: (currentNumber.length > 2 && telnyxConnected) ? '#10b981' : '#e5e7eb',
//                 color: 'white',
//                 fontSize: '15px',
//                 fontWeight: 'bold',
//                 cursor: (currentNumber.length > 2 && telnyxConnected) ? 'pointer' : 'not-allowed',
//                 opacity: (currentNumber.length > 2 && telnyxConnected) ? 1 : 0.5,
//                 transition: 'all 0.3s'
//               }}
//               onMouseEnter={(e) => { 
//                 if (currentNumber.length > 2 && telnyxConnected) {
//                   e.target.style.transform = 'translateY(-2px)';
//                 }
//               }}
//               onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; }}
//             >
//               📞 Call
//             </button>
//           )}
//         </div>
//       </div>
//     </>
//   );
// };

// export default Dialpad;